import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { getMemberDetail } from '@/api/teamMembersTable'
import { getRemovalUsageLine } from '@/pages/app/Team/removalUsageLine'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'

// How long the dialog shows a spinner before giving up and showing plain copy.
// The request keeps going, so a late answer still fills in the line.
const SPINNER_MS = 2000

// Same key as the member detail page so the two share a cache entry and the
// existing ['team-members'] invalidations keep it fresh.
const memberDetailQuery = (enterpriseId: string | null, userId: string | undefined) => ({
  queryKey: ['team-members', 'detail', enterpriseId, userId],
  queryFn: () => getMemberDetail({ userId: userId! }).then((r) => r.data),
  staleTime: 60_000,
  retry: false,
})

export function useRemovalUsageLine(userId: string | undefined) {
  const enterpriseId = useSelector(selectCurrentEnterprise)?.id ?? null
  const queryClient = useQueryClient()
  const enabled = !!enterpriseId && !!userId
  const { data, isPending } = useQuery({ ...memberDetailQuery(enterpriseId, userId), enabled })

  const [spinnerExpired, setSpinnerExpired] = useState(false)
  useEffect(() => {
    setSpinnerExpired(false)
    if (!userId) return
    const timer = setTimeout(() => setSpinnerExpired(true), SPINNER_MS)
    return () => clearTimeout(timer)
  }, [userId])

  return {
    line: data ? getRemovalUsageLine(data) : null,
    isLoading: enabled && isPending && !spinnerExpired,
    // Called on hover/focus of the remove button so the data is usually cached by click time.
    prefetch: (id: string) => {
      if (enterpriseId) void queryClient.prefetchQuery(memberDetailQuery(enterpriseId, id))
    },
  }
}
